import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { employeeService } from '../services/employeeService';
import { Employee, CreateEmployeeData, UpdateEmployeeData, Gender } from '../types';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Select } from '../components/common/Select';
import { Card } from '../components/common/Card';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const genderOptions = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'OTHER', label: 'Other' },
];

const departmentOptions = [
  { value: '', label: 'All Departments' },
  { value: 'Engineering', label: 'Engineering' },
  { value: 'Human Resources', label: 'Human Resources' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Marketing', label: 'Marketing' },
  { value: 'Sales', label: 'Sales' },
  { value: 'Operations', label: 'Operations' },
];

interface EmployeeFormData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  gender: Gender;
  phone: string;
  dateOfBirth: string;
  joinDate: string;
  lastWorkingDay: string;
  department: string;
  designation: string;
  baseSalary: string;
  managerId: string;
  accountHolderName: string;
  bankName: string;
  accountNumber: string;
  ifscCode: string;
  panNumber: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  laptop: string;
  charger: string;
  headphone: string;
  phoneNumber: string;
}

interface PotentialManager {
  id: number;
  firstName: string;
  lastName: string;
  employeeCode: string;
  department?: string;
  designation?: string;
}

const initialFormData: EmployeeFormData = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  gender: 'M',
  phone: '',
  dateOfBirth: '',
  joinDate: new Date().toISOString().split('T')[0],
  lastWorkingDay: '',
  department: '',
  designation: '',
  baseSalary: '',
  managerId: '',
  accountHolderName: '',
  bankName: '',
  accountNumber: '',
  ifscCode: '',
  panNumber: '',
  address: '',
  city: '',
  state: '',
  pincode: '',
  laptop: '',
  charger: '',
  headphone: '',
  phoneNumber: '',
};

export function EmployeesPage() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [potentialManagers, setPotentialManagers] = useState<PotentialManager[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('active');
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [formData, setFormData] = useState<EmployeeFormData>(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'HR';
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchPotentialManagers();
  }, []);

  // Reset to page 1 when search term, department, or status filter changes
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterDepartment, filterStatus]);

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterDepartment, filterStatus, searchTerm]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      
      // If there's a search term, fetch all employees across all pages
      if (searchTerm.trim()) {
        const allEmployees: Employee[] = [];
        let page = 1;
        let hasMore = true;
        
        while (hasMore) {
          const response = await employeeService.getAll(page, 1000, {
            department: filterDepartment || undefined,
            isActive: filterStatus === 'all' ? undefined : filterStatus === 'active',
          });
          
          allEmployees.push(...response.data);
          
          if (page >= (response.pagination?.totalPages || 1)) {
            hasMore = false;
          } else {
            page++;
          }
        }
        
        // Filter employees by search term
        const filtered = allEmployees.filter(emp => {
          const fullName = `${emp.firstName} ${emp.lastName}`.toLowerCase();
          const matchesSearch = fullName.includes(searchTerm.toLowerCase()) ||
            emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.employeeCode.toLowerCase().includes(searchTerm.toLowerCase());
          return matchesSearch;
        });
        
        // Calculate pagination for filtered results
        const itemsPerPage = 10;
        const totalFilteredPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
        setTotalPages(totalFilteredPages);
        
        // Get employees for current page
        const pageToUse = currentPage > totalFilteredPages ? 1 : currentPage;
        const startIndex = (pageToUse - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedEmployees = filtered.slice(startIndex, endIndex);
        setEmployees(paginatedEmployees);
        
        // If we adjusted the page, update it (this will trigger a re-fetch)
        if (pageToUse !== currentPage && totalFilteredPages > 0) {
          setCurrentPage(1);
        }
      } else {
        // No search term - use normal pagination
        const response = await employeeService.getAll(currentPage, 10, {
          department: filterDepartment || undefined,
          isActive: filterStatus === 'all' ? undefined : filterStatus === 'active',
        });
        setEmployees(response.data);
        setTotalPages(response.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchPotentialManagers = async () => {
    try {
      const response = await api.get('/employees/managers/list');
      setPotentialManagers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [name]: e.target.value }));
  };

  const openCreateModal = () => {
    setEditingEmployee(null);
    setFormData(initialFormData);
    setShowModal(true);
  };

  const openEditModal = (employee: Employee) => {
    setEditingEmployee(employee);
    setFormData({
      email: employee.email,
      password: '',
      firstName: employee.firstName,
      lastName: employee.lastName,
      gender: employee.gender,
      phone: employee.phone || '',
      dateOfBirth: employee.dateOfBirth?.split('T')[0] || '',
      joinDate: employee.joinDate.split('T')[0],
      lastWorkingDay: employee.lastWorkingDay?.split('T')[0] || '',
      department: employee.department || '',
      designation: employee.designation || '',
      baseSalary: String(employee.baseSalary),
      managerId: employee.managerId ? String(employee.managerId) : '',
      accountHolderName: employee.accountHolderName || '',
      bankName: employee.bankName || '',
      accountNumber: employee.accountNumber || '',
      ifscCode: employee.ifscCode || '',
      panNumber: employee.panNumber || '',
      address: employee.address || '',
      city: employee.city || '',
      state: employee.state || '',
      pincode: employee.pincode || '',
      laptop: employee.laptop || '',
      charger: employee.charger || '',
      headphone: employee.headphone || '',
      phoneNumber: employee.phoneNumber || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingEmployee) {
        const updateData: UpdateEmployeeData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          gender: formData.gender || undefined,
          phone: formData.phone || undefined,
          dateOfBirth: formData.dateOfBirth || undefined,
          lastWorkingDay: isAdmin ? (formData.lastWorkingDay || null) : undefined,
          department: formData.department || undefined,
          designation: formData.designation || undefined,
          baseSalary: formData.baseSalary ? parseFloat(formData.baseSalary) : undefined,
          accountHolderName: isAdmin ? (formData.accountHolderName || undefined) : undefined,
          bankName: formData.bankName || undefined,
          accountNumber: formData.accountNumber || undefined,
          ifscCode: formData.ifscCode || undefined,
          panNumber: formData.panNumber || undefined,
          address: formData.address || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          pincode: formData.pincode || undefined,
          laptop: isAdmin ? (formData.laptop || undefined) : undefined,
          charger: isAdmin ? (formData.charger || undefined) : undefined,
          headphone: isAdmin ? (formData.headphone || undefined) : undefined,
          phoneNumber: isAdmin ? (formData.phoneNumber || undefined) : undefined,
        };
        await employeeService.update(editingEmployee.id, updateData);
        
        // Handle manager assignment separately (Admin/HR only)
        if (isAdmin && formData.managerId !== (editingEmployee.managerId ? String(editingEmployee.managerId) : '')) {
          await api.put(`/employees/${editingEmployee.id}/manager`, {
            managerId: formData.managerId ? parseInt(formData.managerId) : null,
          });
        }
        
        toast.success('Employee updated successfully');
      } else {
        const createData: CreateEmployeeData = {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          gender: formData.gender,
          joinDate: formData.joinDate,
          baseSalary: parseFloat(formData.baseSalary),
          phone: formData.phone || undefined,
          dateOfBirth: formData.dateOfBirth || undefined,
          lastWorkingDay: isAdmin ? (formData.lastWorkingDay || undefined) : undefined,
          department: formData.department || undefined,
          designation: formData.designation || undefined,
          accountHolderName: formData.accountHolderName || undefined,
          bankName: formData.bankName || undefined,
          accountNumber: formData.accountNumber || undefined,
          ifscCode: formData.ifscCode || undefined,
          panNumber: formData.panNumber || undefined,
          address: formData.address || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          pincode: formData.pincode || undefined,
          laptop: formData.laptop || undefined,
          charger: formData.charger || undefined,
          headphone: formData.headphone || undefined,
          phoneNumber: formData.phoneNumber || undefined,
        };
        const newEmployee = await employeeService.create(createData);
        
        // Assign manager if specified (Admin/HR only)
        if (isAdmin && formData.managerId) {
          await api.put(`/employees/${newEmployee.id}/manager`, {
            managerId: parseInt(formData.managerId),
          });
        }
        
        toast.success('Employee created successfully');
      }
      setShowModal(false);
      fetchEmployees();
    } catch (error) {
      console.error('Error saving employee:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (employee: Employee) => {
    if (!confirm(`Are you sure you want to deactivate ${employee.firstName} ${employee.lastName}?`)) {
      return;
    }

    try {
      await employeeService.deactivate(employee.id);
      toast.success('Employee deactivated successfully');
      fetchEmployees();
    } catch (error) {
      console.error('Error deactivating employee:', error);
    }
  };

  const handleReactivate = async (employee: Employee) => {
    try {
      await employeeService.reactivate(employee.id);
      toast.success('Employee reactivated successfully');
      fetchEmployees();
    } catch (error) {
      console.error('Error reactivating employee:', error);
    }
  };

  // When searching, use paginated filtered employees
  // When not searching, show all employees from current page
  const filteredEmployees = searchTerm.trim() 
    ? employees // Already filtered and paginated in fetchEmployees
    : employees; // Show all from current page

  const formatCurrency = (amount: number | string) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(Number(amount) || 0);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="animate-in space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employee Management</h1>
          <p className="text-gray-500 mt-1">Manage all employees in the organization</p>
        </div>
        <Button onClick={openCreateModal} className="flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Employee
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Input
              placeholder="Search by name, email, or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <Select
              options={departmentOptions}
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
            />
          </div>
          <div>
            <Select
              options={[
                { value: 'active', label: 'Active Employees' },
                { value: 'inactive', label: 'Inactive Employees' },
                { value: 'all', label: 'All Employees' },
              ]}
              value={filterStatus}
              onChange={(e) => setFilterStatus((e.target.value || 'active') as 'all' | 'active' | 'inactive')}
            />
          </div>
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm('');
                setFilterDepartment('');
                setFilterStatus('active');
              }}
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </Card>

      {/* Employee List */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner w-8 h-8 border-4 border-primary-600 border-t-transparent" />
          </div>
        ) : filteredEmployees.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p className="text-gray-500">No employees found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Manager
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Join Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Salary
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEmployees.map((employee) => (
                  <tr key={employee.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold ${
                            employee.gender === 'F' ? 'bg-pink-500' : 'bg-blue-500'
                          }`}>
                            {employee.firstName[0]}{employee.lastName[0]}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {employee.firstName} {employee.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{employee.email}</div>
                          <div className="text-xs text-gray-400">{employee.employeeCode}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{employee.department || '-'}</div>
                      <div className="text-sm text-gray-500">{employee.designation || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {employee.manager ? (
                        <div>
                          <div className="text-sm text-gray-900">
                            {employee.manager.firstName} {employee.manager.lastName}
                          </div>
                          <div className="text-xs text-gray-500">{employee.manager.employeeCode}</div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(employee.joinDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {formatCurrency(employee.baseSalary)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        employee.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {employee.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(employee)}
                          className="text-primary-600 hover:text-primary-900 p-1"
                          title="Edit"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        {employee.isActive ? (
                          <button
                            onClick={() => handleDeactivate(employee)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Deactivate"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            </svg>
                          </button>
                        ) : (
                          <button
                            onClick={() => handleReactivate(employee)}
                            className="text-green-600 hover:text-green-900 p-1"
                            title="Reactivate"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <Button
                variant="outline"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)} />

            <div className="inline-block w-full max-w-3xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
              <form onSubmit={handleSubmit}>
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
                  </h3>
                </div>

                <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Basic Info */}
                    <div className="md:col-span-2">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b">
                        Basic Information
                      </h4>
                    </div>

                    <Input
                      label="First Name *"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                    />
                    <Input
                      label="Last Name *"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                    />
                    <Input
                      label="Email *"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      disabled={!!editingEmployee}
                    />
                    {!editingEmployee && (
                      <Input
                        label="Password *"
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        placeholder="Min 8 chars, uppercase, lowercase, number, special char"
                      />
                    )}
                    <Select
                      label="Gender *"
                      options={genderOptions}
                      value={formData.gender}
                      onChange={handleSelectChange('gender')}
                    />
                    <Input
                      label="Phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+91-9876543210"
                    />
                    <Input
                      label="Date of Birth"
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                    />
                    <Input
                      label="Join Date *"
                      type="date"
                      name="joinDate"
                      value={formData.joinDate}
                      onChange={handleInputChange}
                      required
                    />
                    {isAdmin && (
                      <Input
                        label="Last Working Day"
                        type="date"
                        name="lastWorkingDay"
                        value={formData.lastWorkingDay}
                        onChange={handleInputChange}
                        placeholder="Leave empty if active"
                      />
                    )}

                    {/* Work Info */}
                    <div className="md:col-span-2 mt-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b">
                        Work Information
                      </h4>
                    </div>

                    <Select
                      label="Department"
                      options={departmentOptions.filter(d => d.value !== '')}
                      value={formData.department}
                      onChange={handleSelectChange('department')}
                    />
                    <Input
                      label="Designation"
                      name="designation"
                      value={formData.designation}
                      onChange={handleInputChange}
                      placeholder="e.g., Software Developer"
                    />
                    <Input
                      label="Base Salary (₹) *"
                      type="number"
                      name="baseSalary"
                      value={formData.baseSalary}
                      onChange={handleInputChange}
                      required
                      min="0"
                    />
                    {isAdmin && (
                      <Select
                        label="Manager"
                        options={[
                          { value: '', label: 'No Manager' },
                          ...potentialManagers
                            .filter(m => m.id !== editingEmployee?.id) // Don't allow self as manager
                            .map(m => ({
                              value: String(m.id),
                              label: `${m.firstName} ${m.lastName} (${m.employeeCode})`,
                            })),
                        ]}
                        value={formData.managerId}
                        onChange={handleSelectChange('managerId')}
                      />
                    )}

                    {/* Bank Info */}
                    <div className="md:col-span-2 mt-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b">
                        Bank Details
                      </h4>
                    </div>

                    {isAdmin && (
                      <Input
                        label="Account Holder Name"
                        name="accountHolderName"
                        value={formData.accountHolderName}
                        onChange={handleInputChange}
                        placeholder="Name as it appears on bank account"
                      />
                    )}
                    <Input
                      label="Bank Name"
                      name="bankName"
                      value={formData.bankName}
                      onChange={handleInputChange}
                    />
                    <Input
                      label="Account Number"
                      name="accountNumber"
                      value={formData.accountNumber}
                      onChange={handleInputChange}
                    />
                    <Input
                      label="IFSC Code"
                      name="ifscCode"
                      value={formData.ifscCode}
                      onChange={handleInputChange}
                      placeholder="e.g., SBIN0001234"
                    />
                    <Input
                      label="PAN Number"
                      name="panNumber"
                      value={formData.panNumber}
                      onChange={handleInputChange}
                      placeholder="e.g., ABCDE1234F"
                    />

                    {/* Address */}
                    <div className="md:col-span-2 mt-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b">
                        Address
                      </h4>
                    </div>

                    <div className="md:col-span-2">
                      <Input
                        label="Street Address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                      />
                    </div>
                    <Input
                      label="City"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                    />
                    <Input
                      label="State"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                    />
                    <Input
                      label="PIN Code"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Assets (Admin Only) */}
                  {isAdmin && (
                    <>
                      <div className="md:col-span-2 mt-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b">
                          Company Assets (Admin Only)
                        </h4>
                      </div>

                      <Input
                        label="Laptop Asset Number"
                        name="laptop"
                        value={formData.laptop}
                        onChange={handleInputChange}
                        placeholder="e.g., LAP-001"
                      />
                      <Input
                        label="Charger Asset Number"
                        name="charger"
                        value={formData.charger}
                        onChange={handleInputChange}
                        placeholder="e.g., CHG-001"
                      />
                      <Input
                        label="Headphone Asset Number"
                        name="headphone"
                        value={formData.headphone}
                        onChange={handleInputChange}
                        placeholder="e.g., HP-001"
                      />
                      <Input
                        label="Company Phone Number"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        placeholder="e.g., +91-9876543210"
                      />
                    </>
                  )}
                </div>

                <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 border-t border-gray-200">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Saving...' : editingEmployee ? 'Update Employee' : 'Create Employee'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
